import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Package, LogOut, ShoppingBag, User, Palette, Link2, Trash2, Check } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useCart } from "@/context/CartContext";
import DesignPreview from "@/components/DesignPreview";
import { CUSTOM_TEE_PRICE } from "@/lib/constants";

const statusStyle = { paid: "bg-brand-green", pending: "bg-brand-yellow", expired: "bg-brand-pink" };

export default function Account() {
  const { user, logout } = useAuth();
  const { addItem } = useCart();
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [designs, setDesigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [copiedId, setCopiedId] = useState(null);

  const loadDesigns = () => api.get("/designs").then(({ data }) => setDesigns(data)).catch(() => {});

  useEffect(() => {
    Promise.all([
      api.get("/orders").then(({ data }) => setOrders(data)).catch(() => {}),
      loadDesigns(),
    ]).finally(() => setLoading(false));
  }, []);

  const reorder = (d) => {
    addItem({
      key: `custom-${Date.now()}`, product_id: "custom", name: "Custom Designed Tee", image: null,
      price: CUSTOM_TEE_PRICE, size: d.size || "M", color: d.custom?.shirtColorName || "Custom", quantity: 1, custom: d.custom,
    });
    toast.success("Added to cart!", { description: d.name });
  };

  const copyShare = (d) => {
    navigator.clipboard.writeText(`${window.location.origin}/design/${d.share_id}`);
    setCopiedId(d.design_id);
    setTimeout(() => setCopiedId(null), 1800);
  };

  const removeDesign = async (id) => {
    try { await api.delete(`/designs/${id}`); setDesigns((p) => p.filter((d) => d.design_id !== id)); toast.success("Design deleted"); }
    catch { toast.error("Delete failed"); }
  };

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 py-12" data-testid="account-page">
      <div className="bg-brand-sky neo-lg rounded-3xl p-7 flex flex-wrap items-center justify-between gap-4 mb-10">
        <div className="flex items-center gap-4">
          {user?.picture ? (
            <img src={user.picture} alt="" className="w-16 h-16 rounded-2xl border-2 border-ink object-cover" />
          ) : (
            <span className="w-16 h-16 rounded-2xl border-2 border-ink bg-white grid place-items-center"><User className="w-8 h-8" /></span>
          )}
          <div>
            <h1 className="font-display font-bold text-3xl">Hey, {user?.name?.split(" ")[0] || "there"}!</h1>
            <p className="font-semibold text-ink/70" data-testid="account-email">{user?.email}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {user?.role === "admin" && (
            <Link to="/admin" className="bg-white font-bold px-5 py-3 rounded-xl neo-press border-2 border-ink" data-testid="account-admin-link">Admin</Link>
          )}
          <button onClick={() => { logout(); navigate("/"); }} className="bg-ink text-paper font-bold px-5 py-3 rounded-xl neo-press border-2 border-ink inline-flex items-center gap-2" data-testid="account-logout">
            <LogOut className="w-4 h-4" /> Logout
          </button>
        </div>
      </div>

      {/* Saved designs */}
      <div className="flex items-center justify-between gap-2 mb-6">
        <div className="flex items-center gap-2"><Palette className="w-6 h-6" /><h2 className="font-display font-bold text-2xl">My designs</h2></div>
        <Link to="/designer" className="font-bold hover:text-brand-pink text-sm">+ New design</Link>
      </div>
      {designs.length === 0 ? (
        <div className="bg-white neo rounded-2xl p-8 text-center mb-12" data-testid="no-designs">
          <p className="font-display font-bold text-lg">No saved designs yet</p>
          <p className="font-semibold text-ink/70 mt-1">Fabricate one in the Design Studio and save it here.</p>
          <Link to="/designer" className="mt-5 inline-flex bg-ink text-paper font-bold px-6 py-3 rounded-xl neo-press border-2 border-ink">Open Studio</Link>
        </div>
      ) : (
        <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3 mb-12">
          {designs.map((d) => (
            <div key={d.design_id} className="bg-white neo rounded-2xl p-4" data-testid={`design-${d.design_id}`}>
              <div className="bg-brand-sky/40 border-2 border-ink rounded-xl p-4 grid place-items-center mb-3">
                <DesignPreview custom={d.custom} className="w-28 h-28" />
              </div>
              <h3 className="font-display font-bold truncate">{d.name}</h3>
              <p className="text-xs font-bold uppercase tracking-wider text-ink/50 mb-3">{d.custom?.shirtColorName} · {d.size}</p>
              <div className="flex gap-2">
                <button onClick={() => reorder(d)} className="flex-1 bg-ink text-paper font-bold text-sm px-3 py-2 rounded-lg neo-press border-2 border-ink inline-flex items-center justify-center gap-1" data-testid={`reorder-${d.design_id}`}>
                  <ShoppingBag className="w-4 h-4" /> Order
                </button>
                <button onClick={() => navigate(`/designer?share=${d.share_id}`)} className="grid place-items-center w-9 h-9 bg-brand-mint neo-sm rounded-lg neo-press" title="Edit" data-testid={`edit-design-${d.design_id}`}><Palette className="w-4 h-4" /></button>
                <button onClick={() => copyShare(d)} className="grid place-items-center w-9 h-9 bg-brand-yellow neo-sm rounded-lg neo-press" title="Copy share link" data-testid={`share-design-${d.design_id}`}>
                  {copiedId === d.design_id ? <Check className="w-4 h-4" /> : <Link2 className="w-4 h-4" />}
                </button>
                <button onClick={() => removeDesign(d.design_id)} className="grid place-items-center w-9 h-9 bg-brand-pink neo-sm rounded-lg neo-press" title="Delete" data-testid={`delete-design-${d.design_id}`}><Trash2 className="w-4 h-4" /></button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Orders */}
      <div className="flex items-center gap-2 mb-6"><Package className="w-6 h-6" /><h2 className="font-display font-bold text-2xl">Order history</h2></div>
      {loading ? (
        <div className="space-y-3">{[...Array(2)].map((_, i) => <div key={i} className="h-24 bg-white neo rounded-2xl animate-pulse" />)}</div>
      ) : orders.length === 0 ? (
        <div className="bg-white neo rounded-2xl p-10 text-center" data-testid="no-orders">
          <div className="w-16 h-16 mx-auto bg-brand-yellow neo rounded-2xl grid place-items-center mb-4"><ShoppingBag className="w-8 h-8" /></div>
          <p className="font-display font-bold text-xl">No orders yet</p>
          <Link to="/shop" className="mt-6 inline-flex bg-ink text-paper font-bold px-6 py-3 rounded-xl neo-press border-2 border-ink">Start shopping</Link>
        </div>
      ) : (
        <div className="space-y-4">
          {orders.map((o) => (
            <div key={o.order_id} className="bg-white neo rounded-2xl p-5" data-testid={`order-${o.order_id}`}>
              <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
                <span className="font-bold text-sm text-ink/60">{o.order_id}</span>
                <span className={`${statusStyle[o.status] || "bg-brand-yellow"} neo-sm rounded-full px-3 py-1 text-xs font-bold uppercase tracking-wider`}>{o.status}</span>
              </div>
              <div className="space-y-1">
                {o.items.map((it, i) => (
                  <div key={i} className="flex justify-between font-semibold text-sm"><span>{it.name} · {it.color}/{it.size} × {it.quantity}</span><span>${it.line_total.toFixed(2)}</span></div>
                ))}
              </div>
              <div className="border-t-2 border-ink mt-3 pt-2 flex justify-between font-display font-bold"><span>Total</span><span>${o.amount.toFixed(2)}</span></div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
